import { cloneDeep } from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { TabPanel } from 'react-tabs';
import { colors } from '../../../../../../../../../colorVars';
import { modifySoftOrderProvideDetails } from '../../../../../../../../../stores/websocketStore/utils/outgoingMessages/tradePlan/updateTradePlanMessages/modifySoftOrders';
import { updatePlugins } from '../../../../../../../../../stores/websocketStore/utils/outgoingMessages/tradePlan/updateTradePlanMessages/updatePlugins';
import { useWebsocketStore } from '../../../../../../../../../stores/websocketStore/websocketProvider';
import ApplyRounding from '../../../../../../../../../utils/math/ApplyRounding';
import { CancelOrderButton } from '../../../../../../../../atomic/buttons/cancelOrderButton/CancelOrderButton';
import IncrementButton from '../../../../../../../../atomic/buttons/incrementButton/IncrementButton';
import { EditableTableFieldNoOnBlurUpdate } from '../../../../../../../../atomic/inputs/editableTableField/EditableFieldNoOnBlurUpdate';
import { EditableTableField } from '../../../../../../../../atomic/inputs/editableTableField/EditableTableField';
import { Body3, fontSizes } from '../../../../../../../../atomic/text/Text';
import style from '../../IndividualTacticStyle.module.css';
import { UnderlyingPriceExitTacticPanelHeader } from './UnderlyingPriceExitTacticPanelHeader';

export const UnderlyingPriceExitTacticPanel = observer(
  // eslint-disable-next-line no-unused-vars
  ({ tradePlanId, addTab, ...rest }) => {
    const { tradePlanStore } = useWebsocketStore();
    const tradePlan = tradePlanStore.tradePlans[tradePlanId] ?? null;
    const valuePairs =
      tradePlan?.exitTactics['UNDERLYING_PRICE_EXIT']?.underlyingPricePairs ??
      null;
    const softOrderId =
      tradePlan?.exitTactics['UNDERLYING_PRICE_EXIT']?.tacticId ?? null;
    const softOrder = tradePlanStore.softOrdersById[softOrderId] ?? null;
    const inPosition = tradePlan?.inPosition;
    const [newPriceValue, setNewPriceValue] = useState(0);
    const [newPctValue, setNewPctValue] = useState(0);

    // soft order functions
    const addValuePair = ({ price, qtyPct }) => {
      modifySoftOrderProvideDetails({
        softOrder,
        details: {
          addTarget: {
            price,
            qtyPct: qtyPct / 100,
          },
        },
      });
    };

    const removeValuePair = ({ price }) => {
      modifySoftOrderProvideDetails({
        softOrder,
        details: { removeTarget: { price } },
      });
    };

    const modifyValuePair = ({ price, qtyPct, previousPrice }) => {
      const details = {
        editTarget: { price, qtyPct: Number(qtyPct) / 100, previousPrice },
      };
      modifySoftOrderProvideDetails({ softOrder, details });
    };

    const updateTactic = (field, newValue) => {
      updatePlugins(
        'tactic',
        { [field]: newValue },
        'UNDERLYING_PRICE_EXIT',
        tradePlanId,
        null
      );
    };

    if (!tradePlan?.exitTactics['UNDERLYING_PRICE_EXIT']) return null;
    return (
      <TabPanel {...rest}>
        <div className={style.tableContainer}>
          {inPosition ? (
            <>
              {softOrder ? (
                <table className={style.table}>
                  <UnderlyingPriceExitTacticPanelHeader />
                  <tbody>
                    {softOrder.value.tacticValue.underlyingPriceExits.map(
                      ({ price, qtyPct }, index) => (
                        <tr key={price}>
                          <td className={style.labelTd}>
                            <Body3 color={colors.lightGrey} fontWeight={700}>
                              {index + 1}
                            </Body3>
                          </td>
                          <td className={style.inputTd}>
                            <div className={style.inputWrapper}>
                              <Body3
                                color={colors.lightGrey}
                                fontWeight={700}
                                margin="5px 0 0 0"
                              >
                                $
                              </Body3>
                              <EditableTableField
                                initialValue={ApplyRounding(Number(price), 2)}
                                updateValue={(value) =>
                                  modifyValuePair({
                                    price: value,
                                    qtyPct: Number(qtyPct) * 100,
                                    previousPrice: price,
                                  })
                                }
                                color={colors.lightGrey}
                                backgroundColor="inherit"
                                bodySize={3}
                              />
                            </div>
                          </td>
                          <td className={style.labelTd}>
                            <div className={style.inputWrapper}>
                              <EditableTableField
                                initialValue={Number(qtyPct) * 100}
                                updateValue={(value) =>
                                  modifyValuePair({
                                    price,
                                    qtyPct: value,
                                    previousPrice: price,
                                  })
                                }
                                color={colors.lightGrey}
                                backgroundColor="inherit"
                                bodySize={3}
                              />
                              <Body3
                                color={colors.lightGrey}
                                fontWeight={700}
                                margin="5px 0 0 0"
                              >
                                %
                              </Body3>
                            </div>
                          </td>
                          <td className={style.inputTd}>
                            <div className={style.inputWrapper}>
                              <CancelOrderButton
                                width={15}
                                clickHandler={() => removeValuePair({ price })}
                              />
                            </div>
                          </td>
                        </tr>
                      )
                    )}
                    <tr>
                      <td className={style.labelTd}>
                        <Body3 color={colors.lightGrey} fontWeight={700}>
                          Add
                        </Body3>
                      </td>
                      <td className={style.inputTd}>
                        <div className={style.inputWrapper}>
                          <Body3
                            color={colors.lightGrey}
                            fontWeight={700}
                            margin="5px 0 0 0"
                          >
                            $
                          </Body3>
                          <EditableTableFieldNoOnBlurUpdate
                            value={newPriceValue}
                            setValue={setNewPriceValue}
                            color={colors.lightGrey}
                            fontSize={fontSizes.body3}
                            backgroundColor="inherit"
                            inputType="number"
                          />
                        </div>
                      </td>
                      <td className={style.labelTd}>
                        <div className={style.inputWrapper}>
                          <EditableTableFieldNoOnBlurUpdate
                            value={newPctValue}
                            setValue={setNewPctValue}
                            color={colors.lightGrey}
                            fontSize={fontSizes.body3}
                            backgroundColor="inherit"
                            inputType="number"
                          />
                          <Body3
                            color={colors.lightGrey}
                            fontWeight={700}
                            margin="5px 0 0 0"
                          >
                            %
                          </Body3>
                        </div>
                      </td>
                      <td className={style.inputTd}>
                        <div className={style.inputWrapper}>
                          <IncrementButton
                            buttonWidth="15px"
                            width={10}
                            color={colors.lightGrey}
                            backgroundColor={colors.blue}
                            stroke={1}
                            paddingPercent={10}
                            handleClick={() =>
                              addValuePair({
                                price: newPriceValue,
                                qtyPct: newPctValue,
                              })
                            }
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                <Body3 color={colors.lightGrey} fontWeight={700}>
                  Edit open orders created by this tactic in the orders table.
                </Body3>
              )}
            </>
          ) : (
            <>
              <table className={style.table}>
                <UnderlyingPriceExitTacticPanelHeader />
                <tbody>
                  {valuePairs.map(({ price, qtyPct }, index) => (
                    <tr key={price}>
                      <td className={style.labelTd}>
                        <Body3 color={colors.lightGrey} fontWeight={700}>
                          {index + 1}
                        </Body3>
                      </td>
                      <td className={style.inputTd}>
                        <div className={style.inputWrapper}>
                          <Body3
                            color={colors.lightGrey}
                            fontWeight={700}
                            margin="5px 0 0 0"
                          >
                            $
                          </Body3>
                          <EditableTableField
                            initialValue={ApplyRounding(Number(price), 2)}
                            updateValue={(value) => {
                              const arr = cloneDeep(valuePairs);
                              arr[index].price = value;
                              updateTactic('underlyingPricePairs', arr);
                            }}
                            color={colors.lightGrey}
                            backgroundColor="inherit"
                            bodySize={3}
                          />
                        </div>
                      </td>
                      <td className={style.labelTd}>
                        <div className={style.inputWrapper}>
                          <EditableTableField
                            initialValue={Number(qtyPct) * 100}
                            updateValue={(value) => {
                              const arr = cloneDeep(valuePairs);
                              arr[index].qtyPct = value / 100;
                              updateTactic('underlyingPricePairs', arr);
                            }}
                            color={colors.lightGrey}
                            backgroundColor="inherit"
                            bodySize={3}
                          />
                          <Body3
                            color={colors.lightGrey}
                            fontWeight={700}
                            margin="5px 0 0 0"
                          >
                            %
                          </Body3>
                        </div>
                      </td>
                      <td className={style.inputTd}>
                        <div className={style.inputWrapper}>
                          <CancelOrderButton
                            width={15}
                            clickHandler={() => {
                              const arr = cloneDeep(valuePairs);
                              arr.splice(index, 1);
                              updateTactic('underlyingPricePairs', arr);
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td className={style.labelTd}>
                      <Body3 color={colors.lightGrey} fontWeight={700}>
                        Add
                      </Body3>
                    </td>
                    <td className={style.inputTd}>
                      <div className={style.inputWrapper}>
                        <Body3
                          color={colors.lightGrey}
                          fontWeight={700}
                          margin="5px 0 0 0"
                        >
                          $
                        </Body3>
                        <EditableTableFieldNoOnBlurUpdate
                          value={newPriceValue}
                          setValue={setNewPriceValue}
                          color={colors.lightGrey}
                          fontSize={fontSizes.body3}
                          backgroundColor="inherit"
                          inputType="number"
                        />
                      </div>
                    </td>
                    <td className={style.labelTd}>
                      <div className={style.inputWrapper}>
                        <EditableTableFieldNoOnBlurUpdate
                          value={newPctValue}
                          setValue={setNewPctValue}
                          color={colors.lightGrey}
                          fontSize={fontSizes.body3}
                          backgroundColor="inherit"
                          inputType="number"
                        />
                        <Body3
                          color={colors.lightGrey}
                          fontWeight={700}
                          margin="5px 0 0 0"
                        >
                          %
                        </Body3>
                      </div>
                    </td>
                    <td className={style.inputTd}>
                      <div className={style.inputWrapper}>
                        <IncrementButton
                          buttonWidth="15px"
                          width={10}
                          color={colors.lightGrey}
                          backgroundColor={colors.blue}
                          stroke={1}
                          paddingPercent={10}
                          handleClick={() => {
                            const arr = [
                              ...valuePairs,
                              {
                                price: newPriceValue,
                                qtyPct: Number(newPctValue) / 100,
                              },
                            ];
                            updateTactic('underlyingPricePairs', arr);
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </>
          )}
        </div>
      </TabPanel>
    );
  }
);

UnderlyingPriceExitTacticPanel.tabsRole = 'TabPanel';
